import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import { X } from 'lucide-react';

const MARK_DEAL_TEAM_MILESTONE = gql`
  mutation MarkDealTeamMilestone(
    $deal_team_id: ID!
    $milestone_id: String!
    $note: String
    $evidence_vault_document_id: ID
  ) {
    mark_deal_team_milestone(
      deal_team_id: $deal_team_id
      milestone_id: $milestone_id
      note: $note
      evidence_vault_document_id: $evidence_vault_document_id
    ) {
      deal_team {
        id
        status
        progress {
          legal_review
          letter_of_intent
          close
        }
      }
      errors
    }
  }
`;

// Per-milestone copy. The Close variant gets weightier confirmation text
// because it also flips team.status='closed' and surfaces the
// "you can start a new deal team" framing in the runbook.
const COPY = {
  legal_review: {
    title: 'Mark Legal Review complete',
    description:
      'Confirm your lawyer has finished their pre-LOI review (corporate records, contracts, tax structure, disclosure schedules).',
    submitLabel: 'Mark complete',
  },
  letter_of_intent: {
    title: 'Mark Letter of Intent complete',
    description:
      'Confirm a buyer has submitted a Letter of Intent with concrete terms.',
    submitLabel: 'Mark complete',
  },
  close: {
    title: 'Close deal',
    description:
      'This will mark the deal closed and archive the workspace. You can start a new deal team after this.',
    submitLabel: 'Close deal',
  },
};

const MilestoneCompletionModal = ({
  open,
  onClose,
  onSuccess,
  dealTeamId,
  milestoneId,
  documents = [],
}) => {
  const [note, setNote] = useState('');
  const [evidenceDocId, setEvidenceDocId] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [markMilestone] = useMutation(MARK_DEAL_TEAM_MILESTONE);

  // Reset on close so the next open is clean.
  useEffect(() => {
    if (!open) {
      setNote('');
      setEvidenceDocId('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const copy = COPY[milestoneId] || COPY.legal_review;
  const isClose = milestoneId === 'close';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await markMilestone({
        variables: {
          deal_team_id: dealTeamId,
          milestone_id: milestoneId,
          note: note.trim() || null,
          evidence_vault_document_id: evidenceDocId || null,
        },
      });

      const payload = result.data?.mark_deal_team_milestone;
      const errs = payload?.errors || [];
      if (errs.length > 0) throw new Error(errs.join(', '));

      if (window.ahoy) {
        window.ahoy.track('succeedo_deal_team_milestone_completed', {
          deal_team_id: dealTeamId,
          milestone_id: milestoneId,
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not mark milestone. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="milestone-completion-title"
    >
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
          <h2
            id="milestone-completion-title"
            className="font-serif text-[20px] font-light text-[hsl(var(--foreground))]"
          >
            {copy.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
            {copy.description}
          </p>

          <div>
            <label
              htmlFor="milestone-note"
              className="block text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5"
            >
              Note <span className="text-[hsl(var(--muted-foreground))]/70 normal-case font-sans">(optional)</span>
            </label>
            <textarea
              id="milestone-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder={
                isClose
                  ? 'e.g., Closed at $X — funds wired, ownership transferred.'
                  : 'Brief note for your records.'
              }
              className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[14px] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/30"
            />
          </div>

          {documents.length > 0 && (
            <div>
              <label
                htmlFor="milestone-evidence"
                className="block text-[12px] font-mono uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5"
              >
                Evidence document <span className="text-[hsl(var(--muted-foreground))]/70 normal-case font-sans">(optional)</span>
              </label>
              <select
                id="milestone-evidence"
                value={evidenceDocId}
                onChange={(e) => setEvidenceDocId(e.target.value)}
                disabled={submitting}
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[14px] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/30"
              >
                <option value="">No evidence document</option>
                {documents.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-full text-[13px] font-medium transition-opacity ${
                isClose
                  ? 'bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90'
                  : 'border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {submitting ? 'Saving…' : copy.submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

MilestoneCompletionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  dealTeamId: PropTypes.string.isRequired,
  milestoneId: PropTypes.oneOf(['legal_review', 'letter_of_intent', 'close']),
  documents: PropTypes.array,
};

export default MilestoneCompletionModal;
